import { validationMixin } from 'vuelidate'
import { clearTypeNames, deepClone, get, isFalsy, invalidField } from '@/utils'
import { listProfileTypes } from '@/configs/profileTypes'

export const mixForm = {
  mixins: [validationMixin],
  validations () {
    return this.validationSchema
  },
  props: {
    loading: {
      type: Boolean,
      default: null
    }
  },
  data () {
    return {
      validating: false
    }
  },
  computed: {
    validationRulesVars () {
      return this.$props
    },
    validationRules () {
      return !this.$options.validationRules
        ? {}
        : this.$options.validationRules(this.validationRulesVars)
    },
    validationSchema () {
      const fields = Object.entries(this.validationRules).reduce(
        (schema, [field, rules]) => {
          schema[field] = Object.entries(rules).reduce((validators, [name, options]) => {
            validators[name] = {}
            if (options.validator === undefined) {
              for (const field in options) {
                validators[name][field] = options[field].validator
              }
            } else {
              validators[name] = options.validator
            }
            return validators
          }, {})
          return schema
        },
        {}
      )
      return { fields }
    },
    validationErrors () {
      return Object.keys(this.validationRules).reduce((allErrors, fieldName) => {
        if (!allErrors[fieldName]) allErrors[fieldName] = []
        const isDirty = this.$v.fields[fieldName]?.$dirty
        const isPending = this.$v.fields[fieldName]?.$pending
        if (!isDirty || isPending) return allErrors
        Object.entries(this.validationRules[fieldName]).forEach(
          ([validatorName, validatorConfig]) => {
            // nested field
            // validatorName - nested field name
            if (typeof this.$v.fields[fieldName][validatorName] === 'object') {
              if (isFalsy(allErrors[fieldName])) allErrors[fieldName] = {}
              for (const validatorField in validatorConfig) {
                for (const nestedValidator in validatorConfig[validatorField]) {
                  allErrors[fieldName][validatorName] = {}
                  if (this.$v.fields[fieldName][validatorName][nestedValidator]) {
                    allErrors[fieldName][validatorName] =
                      validatorConfig[validatorField][nestedValidator]
                  }
                }
              }
            }
            if (!this.$v.fields[fieldName][validatorName]) {
              allErrors[fieldName].push(validatorConfig.message)
            }
          }
        )
        return allErrors
      }, {})
    },
    redirectToSharedAccess () {
      const profileType = listProfileTypes.find(type => type.name === this.$route.name)
      if (profileType) {
        return `/shared-access?t_contains:profileName=${this.getTitle}&t_profileType=${profileType.value}`
      } else {
        return ''
      }
    }
  },
  methods: {
    /**
     * clear empty (null) fields
     * @param data
     * @param objectToFill
     * @param skipFields
     * @returns {{}}
     */
    prepareData (data, objectToFill, skipFields = []) {
      return Object.keys(objectToFill).reduce((acc, field) => {
        if (!data || typeof data !== 'object' || !Object.keys(data).length) return
        if (
          !Array.isArray(data[field]) &&
          typeof data[field] === 'object' &&
          get(data[field], '__typename') !== undefined &&
          !!objectToFill[field] &&
          typeof objectToFill[field] === 'object' &&
          !Object.keys(data[field]).length
        ) {
          acc[field] = this.prepareData(data[field], objectToFill[field])
        } else if (data[field] === null) {
          acc[field] = objectToFill[field]
        } else if (data[field] !== undefined) {
          acc[field] = data[field]
        }
        // skip field
        if (skipFields.includes(field)) {
          acc[field] = objectToFill[field]
        }
        // remove '__typename' recursively
        clearTypeNames(acc[field])
        return acc
      }, {})
    },
    /**
     * populating this.fields by data fields
     * @param data
     * @param cb()
     * @param skipFields
     */
    populate (data, cb = null, skipFields = '') {
      const preparedData = this.prepareData(data, this.fields, skipFields)
      this.fields = preparedData
      if (cb) cb(preparedData)
    },
    composeFormData (fields) {
      // clear data from null values
      const payload = Object.keys(fields).reduce((acc, field) => {
        // if (this.fields[field] !== null)
        acc[field] = this.fields[field]
        return acc
      }, {})
      return deepClone(payload)
    },
    async beforeValidate () {
      return true
    },
    /**
     * Must return a promise always resolved with Boolean value
     */
    async validate () {
      return new Promise((resolve, reject) => {
        this.validating = true
        this.$v.$reset()
        this.$v.$touch()
        if (!this.$v.fields.$pending) {
          resolve(!this.$v.fields.$error)
        } else {
          const unwatch = this.$watch('$v.fields.$pending', function (pending) {
            if (pending) return
            resolve(!this.$v.fields.$error)
            unwatch()
          })
        }
      })
    },
    mixScrollToError (v) {
      if (v?.fields) {
        const elem = document.querySelector(`#${invalidField(v.fields)}`)
        if (elem) {
          elem.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
      }
    },
    async submit () {
      try {
        return await this._doSubmit()
      } catch (e) {}
    },
    async _doSubmit () {
      // execute pre-validation logic and exit if pre-validation didn't passed
      const next = await this.beforeValidate()
      if (!next) return

      // validate the form and exit if validation didn't passed
      const isValid = await this.validate()
      if (!isValid) return

      // emit and return form data if validation passed successfully
      return this.emitData()
    },
    emitData () {
      const formData = this.composeFormData(this.fields)
      this.$emit('submit', formData)
      return formData
    }
  }
}
